import type { Language } from '@eformulieren/engine'
import type { Text } from '@eformulieren/model'
import { type ReactNode, type RefObject, type SyntheticEvent, useEffect, useRef, useState } from 'react'
import { CheckIcon } from '../icons/CheckIcon.js'
import { inLanguage, localeOf } from '../language/text.js'
import { DeadlineBadge } from './DeadlineBadge.js'
import { deadlineText } from './deadline.js'
import { type SeriesPosition, SeriesNavigator } from './SeriesNavigator.js'
import { STICKY_TOP_CHANGED } from './useFollowSticky.js'

/**
 * De alleen-lezen contextkop boven het formulier (R-125, R-126, R-128).
 *
 * **De titel hoort hier, en niet als los element in de invulkolom.** Tot deze wijziging
 * rendeerde `Formulier.tsx` zijn eigen `<h1>` in `.ef-formulier__kolom`, los van deze kaart en
 * ingesprongen naast de inhoudsopgave; het canvas (`Main.dc.html`) toont de titel juist als
 * onderdeel van dezelfde kaart als het dossier en de uitvraag. Dat was geen stijlverschil te
 * herstellen met CSS: de titel zat in een ander component dan de kaart. `titel` is daarom hier
 * verplicht (niet optioneel): elke echte aanroeper (`Invulformulier.tsx`) heeft hem via
 * `projectie.titel` altijd, dus deze kop toont voortaan altijd minstens de titel en is nooit meer
 * leeg. `Formulier.tsx` valt terug op zijn eigen `<h1>` alleen als er helemaal geen `contextkop`
 * is meegegeven (de directe testaanroepen die dit component niet gebruiken).
 *
 * **Alleen wat er echt is, niets verzonnen.** `dossierId` en `uitvraagRef` zijn de ondoorzichtige
 * verwijzingen die de instantie al draagt (R-100); er is geen weergavenaam voor het dossier en
 * `uitvraagRef` is aantoonbaar niet hetzelfde als "ronde" (`apps/koppelvlak/src/registry.ts`).
 * Een `ronde`-veld op de instantie ontbreekt met opzet en blijft dat voorlopig: `ronde` bestaat als
 * begrip niet op de instantie (`registry.ts` koppelt `uitvraag` en `ronde` bewust niet), en een veld
 * ervoor toevoegen is een migratie op `apps/store` (`Instantie` is 1-op-1 gebonden aan zijn kolommen,
 * `schema.test.ts`) voor een kop die leeg blijft zolang er geen processysteem is dat hem vult. Wat de
 * pod wel heeft is de naam van de call, als de definitie hem als verwijzing draagt; zie hieronder.
 *
 * **De kop van 2 november 2026** (besluit van de productowner, eisenboek 12.47, ontwerp 6.1). Boven de
 * titel staat geen los label meer met de naam van het blok ("Aanvraag" boven "Aanvraag Perspectief
 * 2026" zei twee keer hetzelfde), maar een contextregel: de call en de stap, "Perspectief 2026 ·
 * Aanvraag". De call komt uit wat de pod echt heeft: de verwijzing naar `call.titel` van het contract
 * `call-publiceren` (`callTitleOf`), als de definitie die verwijzing draagt en deze lezer hem ziet. De
 * stap is de weergavenaam van het bouwblok, uit de registry-index in het image van de pod (ontwerp
 * 9.5, R-157). Zonder call staat alleen de stap er, en ook die niet als de titel hem al noemt. Geen
 * dossiernummer en geen uitvraagsleutel, om dezelfde reden als op 30 oktober.
 *
 * Onder de titel één metaregel met de deadline en de resterende dagen en de uitleg bij het sterretje
 * (R-09, R-128). Rechts van de titel, op dezelfde rij, de werkbalk van het formulier: Delen, Bronnen,
 * Opmerkingen en Geschiedenis (`gereedschap`). Daaronder de neutrale voortgang (`voortgang`). En zodra
 * de kop uit beeld scrolt, vanaf tabletbreedte, een compacte balk met titel, deadline en voortgang op
 * één regel (`CompactHeader`).
 */
export interface HeaderProgress {
  /** De zin, "0 van 9 secties ingevuld · 21 punten open", al in de taal van de lezer. */
  readonly tekst: string
  /** Hoeveel er klaar is van hoeveel, voor de balk; zonder dit geen balk. */
  readonly deel?: { readonly klaar: number; readonly totaal: number }
  /**
   * De foutkleur: alleen nadat de invuller "Controleren en indienen" koos en er nog iets openstaat
   * (besluit van 2 november 2026). Tot dan is een leeg verplicht veld in een concept normaal.
   */
  readonly fout?: boolean
  /**
   * De knop naar het overzicht van wat er openstaat (R-24, R-140), of niets als er niets openstaat. Een
   * schakelaar: `open` zegt of het overzicht er staat, `controls` is zijn id (live test van 3 november
   * 2026).
   */
  readonly toon?: {
    readonly label: string
    readonly onToon: (event: SyntheticEvent<HTMLElement>) => void
    readonly open?: boolean
    readonly controls?: string
  }
}

export interface ContextHeaderProps {
  readonly taal: Language
  readonly titel: Text
  /** De weergavenaam van het bouwblok van de publicatie, uit het image van de pod (ontwerp 9.5). */
  readonly bloknaam?: Text
  /** De naam van de call, uit de verwijzing naar `call.titel` (`callTitleOf`), als de pod hem heeft. */
  readonly call?: string
  /** Niet getoond: een sleutel zonder weergavenaam (besluit van 30 oktober 2026, R-125). */
  readonly dossierId?: string
  /** Niet getoond, om dezelfde reden. */
  readonly uitvraagRef?: string
  readonly deadline?: string
  /** Nu, van de klok van het formulier, voor de resterende dagen tot de deadline. */
  readonly nu?: Date
  /** Of de uitleg bij het sterretje in de metaregel staat (R-09, besluit van 1 november 2026). */
  readonly verplicht?: boolean
  readonly series?: SeriesPosition
  readonly onNavigate?: (instantieId: string) => void
  readonly seriesBusy?: boolean
  /**
   * In de toetsgedaante: het label "Toetsbeurt" en wanneer de aanvraag is ingediend (ontwerp van
   * het toetsscherm, 29 oktober 2026). Het moment komt uit de laatste revisie van de invoerlaag
   * (R-173, R-174); zonder revisie staat alleen het label er.
   */
  readonly toets?: { readonly ingediendOp?: string }
  /**
   * Bij nalezen: de bevestiging bovenaan dat er is ingediend, en wanneer (R-86, R-133). Zonder revisie
   * met een moment staat er alleen dat er is ingediend. Met `getoetst` komt er één zin bij dat NWO het
   * formulier controleert (besluit van 30 oktober 2026); die zin beschrijft wat het formulier kan en
   * belooft geen taak of termijn (R-194, R-195).
   */
  readonly ingediend?: { readonly op?: string; readonly getoetst?: boolean }
  /**
   * De kijker handelt als gemachtigde (`Projection.namens`, R-190, R-191, audit delen G-23): dan zegt de
   * kop dat, onder de titel. De naam van de aanvrager kent het product niet (R-154), dus de rol.
   */
  readonly namens?: boolean
  /**
   * Op dezelfde rij als de titel, rechts: de werkbalk van het formulier met Delen, Bronnen, Opmerkingen
   * en Geschiedenis (besluit van 2 november 2026). Op een smal scherm breekt hij onder de titel af, en op
   * een telefoon zijn de knoppen compact, met hun naam voor de schermlezer en 44px raakvlak (R-169).
   */
  readonly gereedschap?: ReactNode
  /** De neutrale voortgang onder de metaregel, en in de compacte kop (besluit van 2 november 2026). */
  readonly voortgang?: HeaderProgress
}

export function ContextHeader({
  taal: language,
  titel: title,
  bloknaam: blockName,
  call,
  deadline,
  nu: now,
  verplicht: required = false,
  series,
  onNavigate,
  seriesBusy,
  toets,
  ingediend: submitted,
  namens: onBehalf = false,
  gereedschap: tools,
  voortgang: progress,
}: ContextHeaderProps): ReactNode {
  const headerRef = useRef<HTMLDivElement>(null)
  const navigator =
    series === undefined || onNavigate === undefined ? null : (
      <SeriesNavigator
        positie={series}
        taal={language}
        onNavigate={onNavigate}
        {...(seriesBusy === undefined ? {} : { bezig: seriesBusy })}
      />
    )
  const titleText = inLanguage(title, language)
  const context = contextLine(call, blockName === undefined ? undefined : inLanguage(blockName, language), titleText)

  return (
    <>
      <div className="ef-contextkop" ref={headerRef}>
        <div className="ef-contextkop__hoofd">
          {toets === undefined ? null : (
            <p className="ef-contextkop__toets">
              <span className="ef-contextkop__toetslabel">{REVIEW_TURN[language]}</span>
              {toets.ingediendOp === undefined ? null : (
                <span className="ef-contextkop__ingediend">
                  {SUBMITTED_ON[language]} {moment(toets.ingediendOp, language)}
                </span>
              )}
            </p>
          )}
          {submitted === undefined ? null : (
            // Bovenaan en als status: wie net heeft ingediend ziet eerst dát, en niet een formulier dat
            // er uitgeschakeld uitziet (audit van 30 oktober 2026). Bij een getoetst formulier één zin
            // erbij dat NWO controleert (besluit van 30 oktober 2026): één bevestiging, niet twee.
            <p className="ef-contextkop__ingediendmelding" role="status">
              <CheckIcon aria-hidden="true" />
              <span>
                {submitted.op === undefined ? SUBMITTED[language] : `${SUBMITTED_ON[language]} ${moment(submitted.op, language)}.`}{' '}
                {SUBMITTED_HINT[language]}
                {submitted.getoetst === true ? ` ${REVIEW_FOLLOWS[language]}` : ''}
              </span>
            </p>
          )}
          {context === undefined ? null : <p className="ef-contextkop__context">{context}</p>}
          {/*
            De titel en de werkbalk op één rij: rechts uitgelijnd op een breed scherm, en eronder zodra
            de rij te smal wordt (besluit van 30 oktober 2026).
          */}
          <div className="ef-contextkop__titelrij">
            <h1 className="ef-contextkop__titel">{titleText}</h1>
            {tools}
          </div>
          {onBehalf ? <p className="ef-contextkop__namens">{inLanguage(ON_BEHALF, language)}</p> : null}
          {/*
            Dossier en uitvraag staan er niet (besluit van 30 oktober 2026, R-125): wat de kop draagt
            zijn sleutels zonder weergavenaam ("dossier-sim-a34727", "2026-1"), en een sleutel zegt
            een mens niets. De call staat erboven zodra de definitie hem als verwijzing draagt.
          */}
          {deadline === undefined && !required ? null : (
            <p className="ef-contextkop__meta">
              {deadline === undefined ? null : <DeadlineBadge deadline={deadline} taal={language} {...(now === undefined ? {} : { nu: now })} />}
              {required ? <span className="ef-contextkop__verplicht">{inLanguage(REQUIRED_NOTE, language)}</span> : null}
            </p>
          )}
          {progress === undefined ? null : <ProgressSummary voortgang={progress} />}
        </div>
        {navigator}
      </div>
      <CompactHeader
        kopRef={headerRef}
        titel={titleText}
        {...(deadline === undefined ? {} : { deadline })}
        {...(now === undefined ? {} : { nu: now })}
        taal={language}
        {...(progress === undefined ? {} : { voortgang: progress.tekst })}
      />
    </>
  )
}

/**
 * De contextregel: de call en de stap, of alleen de stap, of niets (besluit van 2 november 2026).
 *
 * Zonder call valt de stap weg als de titel hem al noemt: "Aanvraag" boven "Aanvraag Perspectief 2026"
 * was precies de herhaling die deze regel moest weghalen.
 */
export function contextLine(call: string | undefined, step: string | undefined, title: string): string | undefined {
  const named = call?.trim()
  if (named !== undefined && named !== '') return step === undefined ? named : `${named} · ${step}`
  if (step === undefined) return undefined
  return title.toLocaleLowerCase().includes(step.toLocaleLowerCase()) ? undefined : step
}

/**
 * De neutrale voortgang onder de metaregel (R-24, besluit van 2 november 2026).
 *
 * **Geen live region.** De zin loopt mee met elke aanslag, en een statusregio zou dan bij elk veld
 * opnieuw praten. Wie wil weten wat er openstaat, kiest de knop; het overzicht krijgt dan de focus en
 * een schermlezer leest zijn kop. De balk is versiering naast de zin en zegt niets wat de zin niet zegt.
 */
function ProgressSummary({ voortgang: progress }: { readonly voortgang: HeaderProgress }): ReactNode {
  const share = progress.deel === undefined || progress.deel.totaal === 0 ? undefined : progress.deel.klaar / progress.deel.totaal
  return (
    <div className="ef-kopvoortgang" data-toon={progress.fout === true ? 'fout' : 'neutraal'}>
      <p className="ef-kopvoortgang__tekst">
        <span>{progress.tekst}</span>
        {progress.toon === undefined ? null : (
          <button
            type="button"
            className="ef-kopvoortgang__toon"
            {...(progress.toon.open === undefined ? {} : { 'aria-expanded': progress.toon.open })}
            {...(progress.toon.controls === undefined ? {} : { 'aria-controls': progress.toon.controls })}
            onClick={progress.toon.onToon}
          >
            {progress.toon.label}
          </button>
        )}
      </p>
      {share === undefined ? null : (
        <span className="ef-kopvoortgang__balk" aria-hidden="true">
          <span className="ef-kopvoortgang__vulling" style={{ inlineSize: `${String(Math.round(share * 100))}%` }} />
        </span>
      )}
    </div>
  )
}

/**
 * De compacte kop die bovenaan plakt zodra de echte kop uit beeld is, vanaf tabletbreedte (besluit van
 * 2 november 2026, R-168): titel, deadline en voortgang op één regel.
 *
 * **Geen sprong in de inhoud.** De echte kop krimpt niet; deze balk ligt erover, in een plakkende houder
 * zonder hoogte, dus wat eronder staat schuift niet. **Binnen de eigen scrollhouder.** Plakken doet
 * `position: sticky`, en dat plakt aan de dichtstbijzijnde voorouder die scrolt: in de pod het venster,
 * in het voorbeeld van de configurator het voorbeeld zelf, nooit over de chrome van de configurator. De
 * waarnemer kijkt tegen diezelfde houder (`scrollParent`). **Voor de schermlezer verborgen**: alles wat
 * hij zegt staat in de echte kop, en er staat niets in om te bedienen. Op een telefoon staat hij er niet
 * (de stylesheet), en zonder `IntersectionObserver` evenmin. Met `prefers-reduced-motion` verschijnt hij
 * zonder overgang.
 */
function CompactHeader({
  kopRef: headerRef,
  titel: title,
  deadline,
  nu: now,
  taal: language,
  voortgang: progress,
}: {
  readonly kopRef: RefObject<HTMLDivElement | null>
  readonly titel: string
  readonly deadline?: string
  readonly nu?: Date
  readonly taal: Language
  readonly voortgang?: string
}): ReactNode {
  const [shown, setShown] = useState(false)
  useEffect(() => {
    const header = headerRef.current
    if (header === null || typeof IntersectionObserver !== 'function') return
    const root = scrollParent(header)
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry === undefined) return
        // Alleen als de kop boven de rand verdween, niet als hij nog onder de vouw staat.
        const top = root === null ? 0 : root.getBoundingClientRect().top
        setShown(!entry.isIntersecting && entry.boundingClientRect.bottom <= top)
      },
      { root, threshold: 0 },
    )
    observer.observe(header)
    return () => observer.disconnect()
  }, [headerRef])
  // De inhoudsopgave plakt eronder en rekent haar plek opnieuw zodra de balk komt of gaat (`useFollowSticky`).
  // De zoekbalk van een rich-text-veld plakt in CSS en leest de hoogte uit een eigenschap op het formulier.
  const barRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const form = headerRef.current?.closest<HTMLElement>('.ef-formulier')
    const height = shown ? (barRef.current?.offsetHeight ?? 0) : 0
    form?.style.setProperty('--ef-kompaktkop-hoogte', `${String(height)}px`)
    document.dispatchEvent(new Event(STICKY_TOP_CHANGED))
  }, [shown, headerRef])
  const deadlineLine = deadline === undefined ? undefined : deadlineText(deadline, now ?? new Date(), language)
  return (
    <div className="ef-kompaktkop" data-zichtbaar={shown ? 'true' : 'false'} aria-hidden="true">
      <div className="ef-kompaktkop__balk" ref={barRef}>
        <span className="ef-kompaktkop__titel">{title}</span>
        {deadlineLine === undefined ? null : (
          <span className="ef-kompaktkop__deadline" data-stand={deadlineLine.stand}>
            {deadlineLine.zin}
          </span>
        )}
        {progress === undefined ? null : <span className="ef-kompaktkop__voortgang">{progress}</span>}
      </div>
    </div>
  )
}

/** De dichtstbijzijnde voorouder die verticaal scrolt, of niets voor het venster. */
function scrollParent(element: HTMLElement): HTMLElement | null {
  for (let current = element.parentElement; current !== null; current = current.parentElement) {
    const { overflowY } = getComputedStyle(current)
    if (overflowY === 'auto' || overflowY === 'scroll') return current
  }
  return null
}

const REQUIRED_NOTE: Text = { nl: 'Velden met * zijn verplicht.', en: 'Fields marked * are required.' }
const REVIEW_TURN: Text = { nl: 'Toetsbeurt', en: 'Review turn' }
const SUBMITTED_ON: Text = { nl: 'Ingediend op', en: 'Submitted on' }
const SUBMITTED: Text = { nl: 'Ingediend.', en: 'Submitted.' }
const SUBMITTED_HINT: Text = {
  nl: 'Hieronder staat wat er is ingediend; wijzigen kan niet meer.',
  en: 'Below is what was submitted; it can no longer be changed.',
}
/** Alleen bij een formulier met een toetslaag (besluit van 30 oktober 2026). */
const REVIEW_FOLLOWS: Text = {
  nl: 'NWO controleert uw aanvraag op volledigheid. U hoort het als er iets moet worden aangevuld.',
  en: 'NWO checks your application for completeness. You will hear from NWO if anything needs to be added.',
}

/** "29 oktober 2026, 13:14" in de taal van de lezer, in de tijdzone van de browser. */
function moment(value: string, language: 'nl' | 'en'): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  const locale = localeOf(language)
  return `${date.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' })}, ${date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}`
}

const ON_BEHALF = { nl: 'U werkt als gemachtigde van de aanvrager.', en: 'You are working as the authorised representative of the applicant.' }
